import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Users, ClipboardText, FileDoc, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { PaperworkCampaignCard } from "../components/PaperworkCampaignCard";
import { LaunchAnnouncementCard } from "../components/LaunchAnnouncementCard";
import { DEMO } from "../demo/demoData";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const STATUS = {
  Active: { label: "Active", cls: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30" },
  Inactive: { label: "Inactive — no longer with us", cls: "bg-stone-100 text-stone-400 border-stone-200" },
  in_progress: { label: "In Progress", cls: "bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40" },
  ready: { label: "Ready to Generate", cls: "bg-primary/10 text-primary border-primary/30" },
  generated: { label: "Plan Generated", cls: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30" },
};

const JOURNEY = {
  signed: { label: "Signed ✓ — all done", cls: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30" },
  careplan_left: { label: "Agreement signed · care plan left", cls: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30" },
  sign_opened: { label: "Link opened — signing pending", cls: "bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40" },
  sign_sent: { label: "Signing link sent — not opened yet", cls: "bg-primary/10 text-primary border-primary/30" },
  ready_to_sign: { label: "Ready to sign — send the link", cls: "bg-primary/10 text-primary border-primary/30" },
  answering: { label: "Link opened — answering now", cls: "bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40" },
  q_sent: { label: "Questionnaire link sent", cls: "bg-primary/10 text-primary border-primary/30" },
  intake: { label: "Intake in progress", cls: "bg-stone-100 text-stone-500 border-stone-200" },
};

export default function ClientsPage() {
  const [clients, setClients] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [dutyBusy, setDutyBusy] = useState(false);
  const [dutyResult, setDutyResult] = useState("");
  const [pocBusy, setPocBusy] = useState(false);
  const [pocResult, setPocResult] = useState("");

  const importPocs = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPocBusy(true);
    setPocResult("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/poc-upload-zip", fd, { timeout: 300000 });
      const parts = [`${data.matched.length} stored`, `${data.dupes.length} already on file`];
      if (data.unmatched.length) parts.push(`${data.unmatched.length} unmatched: ${data.unmatched.map((u) => u.member || u.file).join(", ")}`);
      if (data.failed.length) parts.push(`${data.failed.length} unreadable`);
      setPocResult(parts.join(" · "));
      toast.success(`POC import: ${data.matched.length} stored`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPocBusy(false);
    }
  };
  const [showInactive, setShowInactive] = useState(true);
  const navigate = useNavigate();

  const importDuties = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setDutyBusy(true);
    setDutyResult("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: preview } = await api.post("/imports/hha-duties?dry_run=true", fd, { timeout: 120000 });
      const cols = Object.entries(preview.columns || {}).map(([k, v]) => `${k} → ${v}`).join(", ");
      const warn = preview.unmatched_clients?.length ? `\nUnmatched clients: ${preview.unmatched_clients.join(", ")}` : "";
      if (!window.confirm(`Duties import preview\n\nColumns detected: ${cols}\nRows read: ${preview.rows_read} · new entries: ${preview.new} · duplicates skipped: ${preview.dupes} · rows skipped: ${preview.skipped_rows}${warn}\n\nImport now?`)) {
        setDutyResult("Import cancelled at preview.");
        setDutyBusy(false);
        return;
      }
      const fd2 = new FormData();
      fd2.append("file", file);
      const { data } = await api.post("/imports/hha-duties", fd2, { timeout: 120000 });
      setDutyResult(`Imported ${data.new} duty entries (${data.dupes} duplicates skipped${data.unmatched_clients?.length ? `, unmatched: ${data.unmatched_clients.join(", ")}` : ""}).`);
      toast.success(`Imported ${data.new} duty entries`);
    } catch (err) {
      toast.error(formatApiError(err));
      setDutyResult("");
    } finally {
      setDutyBusy(false);
    }
  };

  const createDemo = async () => {
    setDemoBusy(true);
    try {
      const { data: client } = await api.post("/clients", { intake: DEMO.intake });
      for (const k of ["profile", "gty", "medications", "careplan", "approval"]) {
        if (DEMO[k]) await api.put(`/clients/${client.id}/forms/${k}`, DEMO[k]);
      }
      toast.success(`Demo client created — ${client.forms?.intake?.full_name || "fully filled sample"}`);
      navigate(`/clients/${client.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDemoBusy(false);
    }
  };

  useEffect(() => {
    api.get("/clients")
      .then((r) => setClients(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setClients([]); });
  }, []);

  const clientName = (c) => c.full_name || c.name || c.client_code || "Unnamed";
  const isHiddenRecord = (c) => c.is_duplicate || c.client_code === "MapleHollow" || c.client_code === "MeadowCrossing"
    || clientName(c).toLowerCase() === "office hours" || clientName(c).includes("(Demo)");
  const visibleClients = (clients || [])
    .filter((c) => !isHiddenRecord(c))
    .sort((a, b) => clientName(a).localeCompare(clientName(b)));
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const q = query.trim().toLowerCase();
  // every word of the query must match somewhere in name/phone/email — so "martha walls"
  // finds "Martha J Walls" and "walls martha" works too
  const matchesQuery = (c) => {
    if (!q) return true;
    const hay = [clientName(c), c.phone, c.email].filter(Boolean).join(" ").toLowerCase();
    return q.split(/\s+/).every((w) => hay.includes(w));
  };
  const STATUS_OPTIONS = ["", "Active", "in_progress", "ready", "generated", "Inactive"];
  const STATUS_LABEL = { "": "All statuses", in_progress: "Intake in progress" };
  // searching finds ANYONE regardless of status (incl. inactive); browsing keeps the status filter
  const filteredClients = visibleClients.filter((c) => {
    if (!matchesQuery(c)) return false;
    if (q) return true;
    if (statusFilter) return c.status === statusFilter;
    return showInactive || c.status !== "Inactive";
  });
  const twoWeeksAgo = Date.now() - 14 * 86400000;
  const recentClients = visibleClients
    .filter((c) => c.created_at && new Date(c.created_at).getTime() > twoWeeksAgo)
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
    .slice(0, 8);
  const currentClientCount = visibleClients.filter((c) => c.status !== "Inactive").length;
  const inactiveClientCount = visibleClients.filter((c) => c.status === "Inactive").length;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const stats = {
    total: visibleClients.length,
    ready: visibleClients.filter((c) => c.status === "ready").length,
    generated: visibleClients.filter((c) => c.status === "generated").length,
  };

  return (
    <div data-testid="clients-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Clients</h1>
          <p className="mt-1 text-sm text-stone-500">Assessment progress and AI care plan generation</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => navigate("/clients/intake")} data-testid="new-client-button"
            className="rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-stone-900 active:scale-95">
            <Plus size={16} className="mr-1.5" weight="bold" /> New Client Intake
          </Button>
          <Button variant="outline" onClick={createDemo} disabled={demoBusy} data-testid="demo-client-button"
            className="rounded-full border-stone-300 text-stone-700 transition-colors hover:bg-stone-900 hover:text-white active:scale-95">
            {demoBusy ? "Creating..." : "Demo Client"}
          </Button>
          <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/intake-start`); toast.success("Public intake link copied — anyone with it can start a fresh intake"); }} className="text-xs font-medium text-stone-400 hover:text-stone-700 hover:underline" data-testid="copy-public-intake-link">
            copy public intake link
          </button>
          <button onClick={() => navigate("/clients/new")} className="text-xs font-medium text-stone-400 hover:text-stone-700 hover:underline" data-testid="manual-client-link">
            or fill the form manually
          </button>
        </div>
      </div>

      {recentClients.length > 0 && !q && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-[#4A235A]/20 bg-[#4A235A]/[0.04] px-4 py-3" data-testid="recent-clients-strip">
          <span className="text-xs font-bold uppercase tracking-wide text-[#4A235A]">Recently added</span>
          {recentClients.map((c) => (
            <Link key={c.id} to={`/clients/${c.id}`}
              className="rounded-full border border-[#4A235A]/25 bg-white px-3 py-1 text-xs font-semibold text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white"
              data-testid={`recent-client-${c.id}`}>
              {clientName(c)} · {fmtDate(c.created_at)}
            </Link>
          ))}
        </div>
      )}
      {clients !== null && clients.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Find anyone — name, phone, or email (searches every status)…"
            className="h-11 w-full max-w-md rounded-xl border border-stone-300 bg-white px-4 text-sm shadow-sm"
            data-testid="client-search" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="h-11 rounded-xl border border-stone-300 bg-white px-3 text-sm" data-testid="client-status-filter">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
          </select>
          <p className="text-xs text-stone-500" data-testid="clients-current-count">
            {q ? `${filteredClients.length} match${filteredClients.length === 1 ? "" : "es"} (all statuses)` : `${currentClientCount} current clients`}
          </p>
          <label className="ml-auto flex items-center gap-2 text-xs text-stone-500" data-testid="toggle-inactive">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="toggle-inactive-check" />
            Show inactive ({inactiveClientCount} no longer with us)
          </label>
        </div>
      )}
      {clients === null ? (
        <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : clients.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white p-16 text-center" data-testid="empty-clients">
          <p className="font-heading text-lg font-semibold text-stone-700">No clients yet</p>
          <p className="mt-1 text-sm text-stone-500">Start with a phone intake to create the first client record and generate their de-identified code.</p>
          <Button onClick={() => navigate("/clients/new")} className="mt-6 rounded-full bg-primary text-primary-foreground transition-colors hover:bg-stone-900 active:scale-95" data-testid="empty-new-client-button">
            <Plus size={16} className="mr-1.5" weight="bold" /> Begin Phone Intake
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="clients-table">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
                <th className="px-6 py-3.5 font-medium">Client</th>
                <th className="px-6 py-3.5 font-medium">Start date</th>
                <th className="px-6 py-3.5 font-medium">Where they are</th>
                <th className="px-6 py-3.5 font-medium">Status</th>
                <th className="px-6 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((c) => {
                const s = STATUS[c.status] || STATUS.in_progress;
                return (
                  <tr key={c.id} className="border-b border-stone-100 transition-colors last:border-0 hover:bg-accent/50"
                    data-testid={`client-row-${c.client_code}`}>
                    <td className="px-6 py-4 font-medium text-stone-900">{clientName(c)}</td>
                    <td className="px-6 py-4 text-stone-600" data-testid={`client-start-${c.client_code}`}>{fmtDate(c.service_start_date)}</td>
                    <td className="px-6 py-4">
                      {c.journey
                        ? <Badge variant="outline" className={JOURNEY[c.journey].cls} data-testid={`journey-${c.client_code || c.id}`}>{JOURNEY[c.journey].label}</Badge>
                        : <span className="text-xs text-stone-300">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline" className={s.cls}>{s.label}</Badge>
                        {c.review_due && <Badge variant="outline" className="bg-[#C0392B]/10 text-[#C0392B] border-[#C0392B]/30" data-testid={`review-due-${c.client_code}`}>Review due — {c.review_reason}</Badge>}
                        {c.significant_change && <Badge variant="outline" className="bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40" data-testid={`sig-change-${c.client_code}`}>Significant change</Badge>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-4">
                        <Link to={`/clients/${c.id}/paperwork`} data-testid={`paperwork-client-${c.client_code}`}
                          className="inline-flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-stone-900">
                          Paperwork
                        </Link>
                        <Link to={`/clients/${c.id}`} data-testid={`open-client-${c.client_code}`}
                          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-stone-900">
                          Open <ArrowRight size={14} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="my-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        {[
          { icon: Users, label: "Total Clients", value: stats.total, tid: "stat-total-clients" },
          { icon: ClipboardText, label: "Ready to Generate", value: stats.ready, tid: "stat-ready-clients" },
          { icon: FileDoc, label: "Plans Generated", value: stats.generated, tid: "stat-generated-clients" },
        ].map(({ icon: Icon, label, value, tid }) => (
          <div key={tid} className="flex items-center gap-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md" data-testid={tid}>
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary text-primary">
              <Icon size={22} />
            </span>
            <div>
              <p className="font-heading text-2xl font-semibold text-stone-900">{value}</p>
              <p className="text-xs text-stone-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="duties-import-card">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-stone-800">Weekly HHAeXchange duties import</p>
          <p className="text-xs text-stone-500">Export the duties/tasks report (Excel) and drop it here — it powers each client's Plan vs. actual card.</p>
          {dutyResult && <p className="mt-1 text-xs text-stone-600" data-testid="duties-import-result">{dutyResult}</p>}
        </div>
        <label className={`inline-flex cursor-pointer items-center rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-stone-900 ${dutyBusy ? "pointer-events-none opacity-50" : ""}`} data-testid="duties-import-button">
          {dutyBusy ? "Importing…" : "Upload duties export"}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="duties-import-input" onChange={importDuties} />
        </label>
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="poc-import-card">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-stone-800">POC library — Plans of Care</p>
          <p className="text-xs text-stone-500">Drop a ZIP of HHAeXchange POC PDFs — each one is read, matched to the right client, and used to check the care plan before printing. One client's single POC goes on their own page (More actions → Upload POC).</p>
          {pocResult && <p className="mt-1 text-xs text-stone-600" data-testid="poc-import-result">{pocResult}</p>}
        </div>
        <label className={`inline-flex cursor-pointer items-center rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-stone-900 ${pocBusy ? "pointer-events-none opacity-50" : ""}`} data-testid="poc-import-button">
          {pocBusy ? "Reading & matching…" : "Upload POC ZIP"}
          <input type="file" accept=".zip" className="hidden" data-testid="poc-import-input" onChange={importPocs} />
        </label>
      </div>

      <PaperworkCampaignCard />

      <LaunchAnnouncementCard />
    </div>
  );
}
